import { Graf } from "../../models/nets/GraphExtension";
import { Point } from "../../models/Point";
import { FlowDirection } from "../../shared/types";

/**
 * Network color constants for visualization
 */
export const NetworkColors = {
    TERMINAL_IN: '#006400',      // Dark green for terminal IN flow
    TERMINAL_OUT: '#8b0000',     // Dark red for terminal OUT flow
    TERMINAL_UNKNOWN: '#696969', // Dark gray for terminal UNKNOWN
    NODE_IN: '#00ff00',          // Bright green for non-terminal IN flow
    NODE_OUT: '#ff0000',         // Bright red for non-terminal OUT flow
    NODE_UNKNOWN: '#808080',     // Light gray for non-terminal UNKNOWN
    DEFAULT: 'blue'              // Default color
} as const;

/**
 * Safely adds a bidirectional edge between two nodes in a graph
 * @param graf - The graph to add edges to
 * @param node1 - First node
 * @param node2 - Second node
 */
export function addBidirectionalEdge(graf: Graf, node1: Point, node2: Point): void {
    if (!node1 || !node2 || node1.id === node2.id) return;
    
    safeAddEdge(graf, node1, node2);
    safeAddEdge(graf, node2, node1);
}

/**
 * Safely adds an edge between two nodes, avoiding duplicates
 * @param graf - The graph to add edge to
 * @param start - Start node
 * @param end - End node
 */
export function safeAddEdge(graf: Graf, start: Point, end: Point): void {
    if (!start || !end || start.id === end.id) return;

    try {
        if (!graf.hasEdge(start.id, end.id) && !graf.hasEdge(end.id, start.id)) {
            graf.addEdge(start, end);
        }
    } catch (error) {
        // Edge already exists, ignore
    }
}

/**
 * Calculates node color based on flow direction and terminal status
 * @param flowDirection - Flow direction enum value
 * @param isTerminal - Whether this is a terminal node
 * @returns Color string for the node
 */
export function calculateNodeColor(flowDirection: FlowDirection | null, isTerminal: boolean): string {
    if (flowDirection === FlowDirection.IN) {
        return isTerminal ? NetworkColors.TERMINAL_IN : NetworkColors.NODE_IN;
    } else if (flowDirection === FlowDirection.OUT) {
        return isTerminal ? NetworkColors.TERMINAL_OUT : NetworkColors.NODE_OUT;
    } else if (flowDirection === FlowDirection.UNKNOWN) {
        return isTerminal ? NetworkColors.TERMINAL_UNKNOWN : NetworkColors.NODE_UNKNOWN;
    }
    return NetworkColors.DEFAULT;
}

/**
 * Removes flow direction markers from a label
 * @param label - Label that may contain flow direction markers
 * @returns Label without flow direction markers
 */
export function removeFlowDirectionMarkers(label: string): string {
    return label.replace(/\s*\[(in→out|out→in|in|out)\]/g, '');
}

/**
 * Merges nodes from source graph to target graph
 * @param targetGraph - Graph to merge into
 * @param sourceGraph - Graph to merge from
 */
export function mergeGraphNodes(targetGraph: Graf, sourceGraph: Graf): void {
    const sourceNodeIds = sourceGraph.nodes();

    sourceNodeIds.forEach(nodeId => {
        if (!nodeId) return;

        const nodeAttrs = sourceGraph.getNodeAttributes(nodeId);

        if (nodeAttrs && typeof nodeAttrs.x === 'number' && typeof nodeAttrs.y === 'number') {
            // Create a Point object from the node attributes
            const point = new Point(nodeAttrs.x, -nodeAttrs.y);
            point.id = nodeId;

            // Merge the node into the target graph
            targetGraph.mergeNode(point);

            // Copy ALL node attributes to ensure nothing is lost during merge
            // This is important when merging graphs after zoom/pan operations
            Object.keys(nodeAttrs).forEach(key => {
                if (key !== 'x' && key !== 'y') { // x and y are handled by mergeNode
                    targetGraph.setNodeAttribute(nodeId, key, nodeAttrs[key]);
                }
            });
        }
    });
}

/**
 * Merges edges from source graph to target graph
 * @param targetGraph - Graph to merge into
 * @param sourceGraph - Graph to merge from
 */
export function mergeGraphEdges(targetGraph: Graf, sourceGraph: Graf): void {
    sourceGraph.forEachEdge((edge, attributes, source, target) => {
        if (!source || !target) return;

        const sourceAttrs = targetGraph.getNodeAttributes(source);
        const targetAttrs = targetGraph.getNodeAttributes(target);

        if (sourceAttrs && targetAttrs) {
            const sourcePoint = new Point(sourceAttrs.x, -sourceAttrs.y);
            sourcePoint.id = source;

            const targetPoint = new Point(targetAttrs.x, -targetAttrs.y);
            targetPoint.id = target;

            // Add edge if it doesn't already exist
            if (!targetGraph.hasEdge(source, target)) {
                targetGraph.addEdge(sourcePoint, targetPoint);
            }
        }
    });
}

/**
 * Type alias for network elements
 */
export type NetworkElement = import("../../models/shapes/Line").Line | 
                             import("../../models/shapes/Block").Block | 
                             import("../../models/resistances/ElbowBlock").ElbowBlock | 
                             import("../../models/resistances/Tee").Tee;

/**
 * Extracts all node IDs (including fitting IDs) from a node or array of nodes
 * @param nodes - Node ID(s) or node object(s) with id property
 * @param graf - Graph to look up node attributes from (optional)
 * @param graphs - Array of graphs to search if graf not provided (optional)
 * @returns Array of unique node IDs including fitting IDs
 */
export function extractNodeIdsWithFittings(
    nodes: (string | any)[] | string | any,
    graf?: import("../../models/nets/GraphExtension").Graf | null,
    graphs?: import("../../models/nets/GraphExtension").Graf[]
): string[] {
    const allIdsToSearch: string[] = [];
    
    // Normalize input to array
    const nodeArray = Array.isArray(nodes) ? nodes : [nodes];
    
    // Find graph if not provided
    let targetGraf = graf;
    if (!targetGraf && graphs) {
        targetGraf = graphs.find((g) => {
            for (const node of nodeArray) {
                const nodeId = typeof node === 'string' ? node : node?.id;
                if (nodeId && g.hasNode(nodeId)) {
                    return true;
                }
            }
            return false;
        }) || null;
    }
    
    if (targetGraf) {
        for (const node of nodeArray) {
            const nodeId = typeof node === 'string' ? node : node?.id;
            if (!nodeId) continue;

            // Add the node ID itself
            allIdsToSearch.push(nodeId);

            // Check for fittings attribute in node
            if (targetGraf.hasNode(nodeId)) {
                const nodeAttrs = targetGraf.getNodeAttributes(nodeId);

                // Extract fitting IDs from the fittings attribute
                if (nodeAttrs && nodeAttrs.fittings && Array.isArray(nodeAttrs.fittings)) {
                    nodeAttrs.fittings.forEach((fitting: any) => {
                        // Fittings use childNodeId property (not id)
                        if (fitting.childNodeId) {
                            allIdsToSearch.push(fitting.childNodeId);
                        }
                        // Also check for id property for backward compatibility
                        if (fitting.id) {
                            allIdsToSearch.push(fitting.id);
                        }
                    });
                }

                // Legacy support: also check old metadata structure
                const nodeAllIds = (targetGraf as any).getNodeAllIds ? (targetGraf as any).getNodeAllIds(nodeId) : [];
                allIdsToSearch.push(...nodeAllIds);
            }
        }
    } else {
        // Fallback: just use the node IDs directly
        for (const node of nodeArray) {
            const nodeId = typeof node === 'string' ? node : node?.id;
            if (nodeId) allIdsToSearch.push(nodeId);
        }
    }

    // Remove duplicates and return
    return [...new Set(allIdsToSearch)];
}

